import Title from "antd/lib/typography/Title";
import { toTitleCase } from "../../../utils/commonfunction";
import { formatPeriodForPrint } from "../callCenterReportPeriodFormat";

/** Fixed row height + single line for screen, PDF, and print */
const DATA_ROW_HEIGHT = 36;

const tdCss = {
  border: "1px solid black",
  padding: "6px 12px",
  display: "table-cell",
  color: "black",
  height: DATA_ROW_HEIGHT,
  minHeight: DATA_ROW_HEIGHT,
  maxHeight: DATA_ROW_HEIGHT,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  lineHeight: `${DATA_ROW_HEIGHT - 12}px`,
  boxSizing: "border-box",
};
const thCssHead = {
  border: "1px solid black",
  padding: "6px 12px",
  display: "table-cell",
  background: "#4561b9",
  color: "white",
  fontWeight: "bold",
  height: DATA_ROW_HEIGHT,
  minHeight: DATA_ROW_HEIGHT,
  maxHeight: DATA_ROW_HEIGHT,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  lineHeight: `${DATA_ROW_HEIGHT - 12}px`,
  boxSizing: "border-box",
};
const sectionTitleCellCss = {
  border: "1px solid black",
  padding: "6px 12px",
  height: DATA_ROW_HEIGHT,
  minHeight: DATA_ROW_HEIGHT,
  maxHeight: DATA_ROW_HEIGHT,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  lineHeight: `${DATA_ROW_HEIGHT - 12}px`,
  boxSizing: "border-box",
};
const headRow = {
  display: "table-row",
  fontWeight: "900",
  color: "#ffffff",
  pageBreakInside: "avoid",
  breakInside: "avoid",
};

/** Keep each table row on one page when printing */
const trKeepTogether = {
  pageBreakInside: "avoid",
  breakInside: "avoid",
};

const tdTotalsCss = {
  ...tdCss,
  fontWeight: "bold",
  background: "#f2f2f2",
};

function headListFor(list) {
  if (!list?.length) return [];
  return Object.keys(list[0]);
}

function parseCellNumber(val) {
  if (val == null || val === "") return null;
  if (typeof val === "number")
    return Number.isFinite(val) ? val : null;
  const s = String(val).replace(/,/g, "").replace(/^\s+|\s+$/g, "");
  if (s === "") return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function sumColumn(list, key) {
  let sum = 0;
  let found = false;
  for (const row of list) {
    const n = parseCellNumber(row[key]);
    if (n !== null) {
      sum += n;
      found = true;
    }
  }
  return found ? sum : null;
}

function formatTotal(n) {
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  return n.toFixed(2);
}

/** One object per header key; first column is label "Totals" unless only column is all-numeric (then sum only). */
function buildTotalsRow(heads, list) {
  if (!list?.length || !heads.length) return null;
  const row = {};
  if (heads.length === 1) {
    const key = heads[0];
    const sum = sumColumn(list, key);
    row[key] = sum !== null ? formatTotal(sum) : "Totals";
    return row;
  }
  heads.forEach((key, idx) => {
    if (idx === 0) {
      row[key] = "Totals";
      return;
    }
    const sum = sumColumn(list, key);
    row[key] = sum !== null ? formatTotal(sum) : "";
  });
  return row;
}

function TotalsDataRow({ heads, totalsRow }) {
  if (!totalsRow) return null;
  return (
    <tr style={trKeepTogether}>
      {heads.map((x) => (
        <td key={x} style={tdTotalsCss}>
          {totalsRow[x]}
        </td>
      ))}
    </tr>
  );
}

export default function CC_SalesSummaryTemp(props) {
  const sections = [
    { title: "Sales Summary", list: props.salesSummary || [] },
    { title: "Sales By Payment Terms", list: props.byPaymentTerms || [] },
    { title: "Sales By Order Mode", list: props.byOrderMode || [] },
    { title: "Sales By Agent", list: props.byAgent || [] },
    { title: "Sales By Area", list: props.byArea || [] },

  ];

  const maxCols = Math.max(
    1,
    ...sections.map((s) => headListFor(s.list).length)
  );

  return (
    <div style={{ border: "1px solid gray", padding: 20, width: "100%" }}>
      <style>
        {`
          @media print {
            #table-to-xls tbody.cc-sales-summary-section {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            #table-to-xls tbody.cc-sales-summary-grid tr {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            #table-to-xls tbody.cc-sales-summary-grid td {
              white-space: nowrap !important;
              height: ${DATA_ROW_HEIGHT}px !important;
              min-height: ${DATA_ROW_HEIGHT}px !important;
              max-height: ${DATA_ROW_HEIGHT}px !important;
              vertical-align: middle !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      <table
        style={{ borderCollapse: "collapse", width: "100%" }}
        id="table-to-xls"
      >
        <tbody>
          <tr>
            <td colSpan={maxCols}>
              <Title level={4} style={{ textAlign: "center", marginBottom: 8 }}>
                Call Center Sales Summary
              </Title>
            </td>
          </tr>
          <tr>
            <td colSpan={maxCols}>
              <Title level={5} style={{ textAlign: "center", marginTop: 0 }}>
                {props.branch ? `Branch: ${props.branch}` : ""}
                {props.branch && (props.dateFrom || props.dateTo) ? " — " : ""}
                {formatPeriodForPrint(
                  props.fetchReportBy,
                  props.dateFrom,
                  props.dateTo
                )}
              </Title>
            </td>
          </tr>

        </tbody>
        {sections.map((section) => (
          <tbody
            key={section.title}
            className="cc-sales-summary-grid cc-sales-summary-section"
          >
            <ReportSection {...section} />
          </tbody>
        ))}
      </table>
    </div>
  );
}

function ReportSection({ title, list }) {
  const heads = headListFor(list);
  const colSpan = Math.max(heads.length, 1);
  const totalsRow = list?.length > 0 && heads.length > 0 ? buildTotalsRow(heads, list) : null;

  return (
    <>
      <tr style={trKeepTogether}>
        <td colSpan={colSpan} style={sectionTitleCellCss}>
          <strong>{title}</strong>
        </td>
      </tr>
      {heads.length > 0 && (
        <tr style={headRow}>
          {heads.map((x) => (
            <td key={x} style={thCssHead}>
              {toTitleCase(String(x || ""))}
            </td>
          ))}
        </tr>
      )}
      {list?.length > 0 ? (
        <>
          {list.map((row, index) => (
            <tr key={index} style={trKeepTogether}>
              {heads.map((x) => (
                <td key={x} style={tdCss}>
                  {row[x]}
                </td>
              ))}
            </tr>
          ))}
          <TotalsDataRow heads={heads} totalsRow={totalsRow} />
        </>
      ) : (
        <tr style={trKeepTogether}>
          <td colSpan={colSpan} style={{ ...tdCss, fontStyle: "italic" }}>
            No records
          </td>
        </tr>
      )}
      <tr style={trKeepTogether}>
        <td colSpan={colSpan} style={{ height: 14, border: "none" }} />
      </tr>
    </>
  );
}
